// src/adapters/index.ts
import {
  createIndexedDBDocumentsAdapter,
  createNoOpHapticAdapter,
  createWebAnalyticsAdapter as createWebAnalyticsAdapter2
} from "@selfxyz/mobile-sdk-alpha/browser";

// src/adapters/analytics.ts
function bridgeAnalyticsAdapter(bridge) {
  return {
    trackEvent(event, payload) {
      bridge.fire("analytics", "trackEvent", { event, payload });
    },
    trackNfcEvent(name, properties) {
      bridge.fire("analytics", "trackNfcEvent", { name, properties });
    },
    logNFCEvent(level, message, context, details) {
      bridge.fire("analytics", "logNfcEvent", {
        level,
        message,
        context,
        details
      });
    }
  };
}

// src/adapters/auth.ts
function bridgeAuthAdapter(bridge) {
  return {
    async getPrivateKey() {
      try {
        const result = await bridge.request("secureStorage", "get", {
          key: "self_private_key",
          requireBiometric: true
        });
        if (result == null) return null;
        if (typeof result === "string") return result;
        return result.value ?? null;
      } catch {
        return null;
      }
    }
  };
}

// src/adapters/biometrics.ts
function bridgeBiometricsAdapter(bridge) {
  return {
    async authenticate(params) {
      return bridge.request("biometrics", "authenticate", params);
    },
    async isAvailable() {
      return bridge.request("biometrics", "isAvailable", {});
    },
    async getBiometryType() {
      return bridge.request("biometrics", "getBiometryType", {});
    }
  };
}

// src/adapters/camera.ts
function bridgeCameraAdapter(bridge) {
  return {
    async scanMRZ(params) {
      return bridge.request("camera", "scanMRZ", params ?? {});
    },
    async isAvailable() {
      return bridge.request("camera", "isAvailable", {});
    }
  };
}

// src/adapters/crypto.ts
import { createWebCryptoAdapter } from "@selfxyz/mobile-sdk-alpha/browser";
function bridgeCryptoAdapter(bridge) {
  const webCryptoAdapter = createWebCryptoAdapter();
  return {
    async hash(input, algo = "sha256") {
      return webCryptoAdapter.hash(input, algo);
    },
    async sign(data, keyRef) {
      const base64Data = uint8ArrayToBase64(data);
      const result = await bridge.request("crypto", "sign", {
        data: base64Data,
        keyRef
      });
      if (typeof result?.signature !== "string" || result.signature.length === 0) {
        throw new Error("Invalid or empty signature from bridge");
      }
      return base64ToUint8Array(result.signature);
    },
    async generateKey(keyRef) {
      const result = await bridge.request("crypto", "generateKey", { keyRef });
      if (!result?.success || typeof result.keyRef !== "string" || result.keyRef.length === 0) {
        throw new Error("Native key generation failed");
      }
      return { keyRef: result.keyRef };
    },
    async getPublicKey(keyRef) {
      const result = await bridge.request("crypto", "getPublicKey", { keyRef });
      if (typeof result?.publicKey !== "string" || result.publicKey.length === 0) {
        throw new Error("Invalid or empty publicKey from bridge");
      }
      return base64ToUint8Array(result.publicKey);
    }
  };
}
function uint8ArrayToBase64(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// src/adapters/documents.ts
function bridgeDocumentsAdapter(bridge) {
  return {
    loadDocumentCatalog() {
      return bridge.request("documents", "loadCatalog");
    },
    async saveDocumentCatalog(catalog) {
      await bridge.request("documents", "saveCatalog", { catalog });
    },
    loadDocumentById(id) {
      return bridge.request("documents", "loadById", { id });
    },
    async saveDocument(id, data) {
      await bridge.request("documents", "save", { id, data });
    },
    async deleteDocument(id) {
      await bridge.request("documents", "delete", { id });
    }
  };
}

// src/adapters/haptic.ts
function bridgeHapticAdapter(bridge) {
  return {
    trigger(type) {
      bridge.fire("haptic", "trigger", { type });
    }
  };
}

// src/adapters/lifecycle.ts
function bridgeLifecycleAdapter(bridge) {
  return {
    ready(payload = {}) {
      bridge.fire("lifecycle", "ready", payload);
    },
    dismiss(payload = {}) {
      bridge.fire("lifecycle", "dismiss", payload);
    },
    async setResult(result) {
      if (bridge.usesBrowserHostTransport) {
        bridge.fire("lifecycle", "setResult", result);
        return;
      }
      await bridge.request("lifecycle", "setResult", result);
    }
  };
}

// src/adapters/nfc-scanner.ts
var NFC_TIMEOUT_MS = 12e4;
function bridgeNFCScannerAdapter(bridge) {
  return {
    scan(opts) {
      const { signal, ...params } = opts;
      const promise = bridge.request(
        "nfc",
        "scanPassport",
        params,
        NFC_TIMEOUT_MS
      );
      if (signal) {
        const onAbort = () => {
          bridge.fire("nfc", "cancelScan", { sessionId: params.sessionId });
        };
        if (signal.aborted) {
          bridge.fire("nfc", "cancelScan", { sessionId: params.sessionId });
          return Promise.reject(new DOMException("Aborted", "AbortError"));
        }
        signal.addEventListener("abort", onAbort, { once: true });
        promise.finally(() => signal.removeEventListener("abort", onAbort));
      }
      return promise;
    }
  };
}
function onNfcProgress(bridge, handler) {
  return bridge.on("nfc", "scanProgress", handler);
}

// src/adapters/storage.ts
function bridgeStorageAdapter(bridge) {
  return {
    async get(key) {
      const result = await bridge.request("secureStorage", "get", { key });
      if (result == null) return null;
      if (typeof result === "string") return result;
      return result.value ?? null;
    },
    async set(key, value) {
      await bridge.request("secureStorage", "set", { key, value });
    },
    async remove(key) {
      await bridge.request("secureStorage", "remove", { key });
    }
  };
}

// src/adapters/keychain-documents.ts
var CATALOG_KEY = "self_document_catalog";
var DOC_PREFIX = "self_doc_";
var EMPTY_CATALOG = { documents: [] };
function safeParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}
function createKeychainDocumentsAdapter(bridge) {
  async function storageGet(key) {
    const result = await bridge.request("secureStorage", "get", { key });
    if (result == null) return null;
    if (typeof result === "string") return result;
    return result.value ?? null;
  }
  async function storageSet(key, value) {
    await bridge.request("secureStorage", "set", { key, value });
  }
  async function storageRemove(key) {
    await bridge.request("secureStorage", "remove", { key });
  }
  return {
    async loadDocumentCatalog() {
      const raw = await storageGet(CATALOG_KEY);
      return raw ? safeParse(raw, EMPTY_CATALOG) : EMPTY_CATALOG;
    },
    async saveDocumentCatalog(catalog) {
      await storageSet(CATALOG_KEY, JSON.stringify(catalog));
    },
    async loadDocumentById(id) {
      const raw = await storageGet(`${DOC_PREFIX}${id}`);
      return raw ? safeParse(raw, null) : null;
    },
    async saveDocument(id, doc) {
      await storageSet(`${DOC_PREFIX}${id}`, JSON.stringify(doc));
    },
    async deleteDocument(id) {
      await storageRemove(`${DOC_PREFIX}${id}`);
    }
  };
}

// src/adapters/sdk-adapter-map.ts
import {
  createWebAnalyticsAdapter,
  createWebNetworkAdapter,
  webNFCScannerShim
} from "@selfxyz/mobile-sdk-alpha/browser";
function createSdkAdapters(opts) {
  const { bridge, navigate, goBack, analytics } = opts;
  const bridgeCrypto = bridgeCryptoAdapter(bridge);
  const crypto = {
    hash: bridgeCrypto.hash,
    sign: bridgeCrypto.sign,
    generateKey: bridgeCrypto.generateKey,
    getPublicKey: bridgeCrypto.getPublicKey
  };
  const bridgeAuth = bridgeAuthAdapter(bridge);
  const auth = {
    getPrivateKey: bridgeAuth.getPrivateKey
  };
  const navigation = {
    goBack,
    goTo: (routeName, params) => {
      const query = params ? `?${new URLSearchParams(params)}` : "";
      navigate(`/${routeName}${query}`);
    }
  };
  return {
    scanner: webNFCScannerShim,
    crypto,
    network: createWebNetworkAdapter(),
    auth,
    documents: createKeychainDocumentsAdapter(bridge),
    navigation,
    analytics: analytics ?? createWebAnalyticsAdapter()
  };
}

// src/adapters/navigation.ts
var routeMap = {
  DocumentCamera: "/onboarding/camera",
  DocumentOnboarding: "/onboarding",
  CountryPicker: "/onboarding/country",
  IDPicker: "/onboarding/id-type",
  DocumentNFCScan: "/onboarding/nfc",
  ManageDocuments: "/documents",
  Home: "/",
  AccountVerifiedSuccess: "/account/verified",
  AccountRecoveryChoice: "/account/recovery",
  SaveRecoveryPhrase: "/account/recovery/phrase",
  ComingSoon: "/coming-soon",
  DocumentDataNotFound: "/error/no-data",
  Settings: "/settings"
};
function webNavigationAdapter(navigate, goBack) {
  return {
    goBack,
    goTo(routeName, _params) {
      const path = routeMap[routeName];
      if (!path) {
        console.warn(`[Navigation] Unknown route: ${routeName}`);
        return;
      }
      navigate(path);
    }
  };
}

// src/adapters/index.ts
function consoleAnalyticsAdapter(options) {
  return createWebAnalyticsAdapter2(options);
}
function indexedDBDocumentsAdapter() {
  return createIndexedDBDocumentsAdapter();
}
function noOpHapticAdapter() {
  const trigger = createNoOpHapticAdapter();
  return {
    trigger(type) {
      trigger(type);
    }
  };
}
export {
  bridgeAnalyticsAdapter,
  bridgeAuthAdapter,
  bridgeBiometricsAdapter,
  bridgeCameraAdapter,
  bridgeCryptoAdapter,
  bridgeDocumentsAdapter,
  bridgeHapticAdapter,
  bridgeLifecycleAdapter,
  bridgeNFCScannerAdapter,
  bridgeStorageAdapter,
  consoleAnalyticsAdapter,
  createKeychainDocumentsAdapter,
  createSdkAdapters,
  indexedDBDocumentsAdapter,
  noOpHapticAdapter,
  onNfcProgress,
  webNavigationAdapter
};
//# sourceMappingURL=adapters.js.map